import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { PhonePrefix } from '@app/models';
import { TranslateService } from '@ngx-translate/core';
import CryptoES from 'crypto-es/lib/index';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { FormErr, FormVal, GenericObject } from './types';

export namespace Utils {
  /**
   * Cycle through given array of subscriptions and unsubscribe from all
   * @param {Subscription[]} subscriptions
   * @returns empty array of subscriptions
   */
  export const unsubscribe = (subscriptions: Subscription[]): Subscription[] => {
    for (const sub of subscriptions) {
      sub.unsubscribe();
    }
    return [];
  };

  /**
   * Given an image returns the same image in base64.
   * @param {File} file
   * @param {boolean} rawBase64
   * @returns encoded image in base64
   */
  export const fileToDataUrl = (file: File | Blob, rawBase64: boolean = false): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const encoded = String(reader.result);
        if (rawBase64) {
          let raw = encoded.replace(/^data:(.*;base64,)?/, '');
          if (raw.length % 4 > 0) {
            raw += '='.repeat(4 - (raw.length % 4));
          }
          resolve(raw);
          return;
        }
        resolve(encoded);
      };
      reader.onerror = reject;
    });
  };
}

export namespace CryptUtils {
  /**
   * Create and returns a Hash object with given algoritm
   * @param {'sha256'} algorithm
   */
  export const createHash = (algorithm: 'SHA1' | 'SHA256') => {
    return CryptoES?.[algorithm];
  };

  /**
   * This method hash the given string in sha256
   * @param {string} str
   * @returns hashed string
   */
  export const hash = (str: string) => {
    const hash = createHash('SHA256');
    return hash(str).toString(CryptoES?.enc.Hex);
  };

  export const encodeBase64 = (str: string) => {
    return window.btoa(str);
  };

  export const decodeBase64 = (str: string) => {
    return window.atob(str);
  };
}

export namespace FormUtils {
  interface AllValidationErrors {
    controlName: string;
    errorName: string;
  }

  interface FormGroupControls {
    [key: string]: AbstractControl;
  }

  interface ICheckForm {
    skipPristine?: boolean;
    trim?: boolean;
  }

  const trimControls = (form: FormGroup) => {
    for (let [key, value] of Object.entries(form.value)) {
      const hasCustomError = Object.keys(form.controls[key].errors ?? {}).some((error) => error.startsWith('__'));
      if (hasCustomError) continue;
      if (typeof value === 'string') {
        form.patchValue({ [key]: value.trim() }, { emitEvent: false, onlySelf: true });
      }
    }
  };

  const getFormValidationErrors = (controls: FormGroupControls, skipPristine: boolean): AllValidationErrors[] => {
    let errors: AllValidationErrors[] = [];
    Object.keys(controls).forEach((key) => {
      const control = controls[key];
      const hasCustomError = Object.keys(control.errors ?? {}).some((error) => error.startsWith('__'));
      if (!skipPristine) {
        control.markAsDirty();
        control.markAsTouched();
        if (!hasCustomError) control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
      if (control.pristine && skipPristine) return;
      if (control instanceof FormGroup) {
        errors = errors.concat(getFormValidationErrors(control.controls, skipPristine));
      }
      const controlErrors: ValidationErrors = control.errors!;
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach((keyError) => {
          errors.push({
            controlName: key,
            errorName: keyError,
          });
        });
      }
    });
    return errors;
  };

  /**
   * Method to validate the given form and to trim each field.
   * At submit, default value of skipPristine and skipTrim are correct,
   * in subscribe of valueChanges pass skipPristine **true** and trim **false**
   * @param {FormErr} formErr
   * @param {FormVal} formVal
   * @param {FormGroup} form
   * @param {boolean} skipPristine *default false*
   * @param {boolean} trim *default true*
   * @param {string[]} controlsWithCustomError
   * @returns if form is valid or not
   */
  export const checkForm = (formErr: FormErr, formVal: FormVal, form: FormGroup, { skipPristine = false, trim = true }: ICheckForm = {}): boolean => {
    for (const elem in formErr) {
      formErr[elem] = '';
    }
    if (trim) trimControls(form);
    const controls = form.controls;
    const errors = getFormValidationErrors(controls, skipPristine);
    errors.forEach((error) => {
      const controlName = error.controlName;
      formErr[controlName] = formVal[controlName][error.errorName];
    });
    return !errors.length;
  };

  /**
   * Enable all form controls
   * @param {FormGroupControls} formControls
   * @param {string[]} controlsToCheck
   */
  export const enableFormControls = (formControls: FormGroupControls, controlsToCheck: string[] = []) => {
    if (controlsToCheck.length) {
      for (const control of Object.keys(formControls)) {
        if (controlsToCheck.includes(control)) {
          formControls[control].enable();
          formControls[control].updateValueAndValidity();
        }
      }
    } else {
      for (const control of Object.keys(formControls)) {
        formControls[control].enable();
        formControls[control].updateValueAndValidity();
      }
    }
  };

  /**
   * Disable all form controls
   * @param {FormGroupControls} formControls
   * @param {string[]} controlsToCheck
   */
  export const disableFormControls = (formControls: FormGroupControls, controlsToCheck: string[] = []) => {
    if (controlsToCheck.length) {
      for (const control of Object.keys(formControls)) {
        if (controlsToCheck.includes(control)) {
          formControls[control].disable();
          formControls[control].updateValueAndValidity();
        }
      }
    } else {
      for (const control of Object.keys(formControls)) {
        formControls[control].disable();
        formControls[control].updateValueAndValidity();
      }
    }
  };

  /**
   * Remove all validators from given control
   * @param {AbstractControl} control
   */
  export const removeValidators = (control: AbstractControl) => {
    control.clearValidators();
    control.updateValueAndValidity();
  };

  /**
   * Add given validator to given control
   * @param {AbstractControl} control
   * @param {ValidatorFn} validator
   */
  export const addValidator = (control: AbstractControl, validator: ValidatorFn) => {
    control.setValidators(validator);
    control.updateValueAndValidity();
  };

  /**
   * Checks whether the given phone number is valid or not
   * @param phoneNumber
   * @param prefix
   */
  export const validatePhoneNumber = (phoneNumber: string, prefix: PhonePrefix | null) => {
    if (!prefix || !phoneNumber) return false;
    const completeNumber = prefix.value + phoneNumber;
    return isValidPhoneNumber(completeNumber, prefix.country as CountryCode);
  };

  /**
   * @returns Object containing all the translations for the p-password component
   */
  export const getPasswordHelper = (translate: TranslateService): GenericObject<string> => {
    return {
      enterPassword: translate.instant('COMPONENTS.PASSWORD.ENTER_PASSWORD'),
      suggestion: translate.instant('COMPONENTS.PASSWORD.SUGGESTIONS'),
      weak: translate.instant('COMPONENTS.PASSWORD.WEAK'),
      medium: translate.instant('COMPONENTS.PASSWORD.MEDIUM'),
      strong: translate.instant('COMPONENTS.PASSWORD.STRONG'),
      lowerCase: translate.instant('COMPONENTS.PASSWORD.LOWERCASE'),
      upperCase: translate.instant('COMPONENTS.PASSWORD.UPPERCASE'),
      numeric: translate.instant('COMPONENTS.PASSWORD.NUMERIC'),
      symbol: translate.instant('COMPONENTS.PASSWORD.SYMBOL'),
      minLength: translate.instant('COMPONENTS.PASSWORD.MIN_LENGTH'),
      maxLength: translate.instant('COMPONENTS.PASSWORD.MAX_LENGTH'),
    };
  };

  export const noNegatives = (e) => {
    // Previene l'immissione di caratteri che non siano i seguenti:
    return (
      (e.keyCode >= 96 && e.keyCode <= 105) || // Numpad 0-9
      (e.keyCode >= 48 && e.keyCode <= 57) || // Numrow 0-9
      e.keyCode == 8 || // Backspace
      e.keyCode == 46 || // Delete
      e.keyCode == 188 || // Comma
      e.keyCode == 190 || // Dot
      e.keyCode == 110 || // Decimal point
      e.keyCode == 9 || // Tab
      (e.keyCode >= 37 && e.keyCode <= 40) ||
      (e.keyCode === 65 && e.ctrlKey === true) || // Allow: Ctrl+A
      (e.keyCode === 67 && e.ctrlKey === true) || // Allow: Ctrl+C
      (e.keyCode === 86 && e.ctrlKey === true) || // Allow: Ctrl+V
      (e.keyCode === 88 && e.ctrlKey === true) || // Allow: Ctrl+X
      (e.keyCode === 65 && e.metaKey === true) || // Allow: Cmd+A (Mac)
      (e.keyCode === 67 && e.metaKey === true) || // Allow: Cmd+C (Mac)
      (e.keyCode === 86 && e.metaKey === true) || // Allow: Cmd+V (Mac)
      (e.keyCode === 88 && e.metaKey === true)
    ); // Allow: Cmd+X (Mac)
  };
}
